/**
 * WegfahrerSettings.js – Mobile-optimierte Admin-Konfiguration
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Plus, Trash2, Save, Fuel, Scale, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const FUEL_OPTIONS = [
  'Diesel', 'Super 95', 'E10', 'Super Plus 98', 'Super 100', 'Super 102',
  'V-Power Diesel', 'Ultimate Diesel', 'Excellium Diesel',
  'Synergy Diesel', 'FuelSave Diesel', 'HVO Diesel',
  'V-Power 98', 'Ultimate 102', 'Excellium 98',
  'Synergy Supreme+ 98', 'Synergy Supreme+ 100',
  'AdBlue', 'LPG', 'CNG', 'LNG', 'Wasserstoff',
];

export const WegfahrerSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(user?.location_ids?.[0] || '');
  const [pumps, setPumps] = useState([]);
  const [lawyer, setLawyer] = useState({ name: '', address: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [customFuel, setCustomFuel] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/admin/locations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setLocations(r.data || []);
        if (!selectedLocation && r.data?.length > 0) setSelectedLocation(r.data[0].id);
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedLocation) return;
    const token = localStorage.getItem('token');
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/api/wegfahrer/config/pumps/${selectedLocation}`, { headers: h }),
      axios.get(`${API}/api/wegfahrer/config/lawyer/${selectedLocation}`, { headers: h }),
    ]).then(([pRes, lRes]) => {
      setPumps(pRes.data?.pumps || []);
      setLawyer(lRes.data || { name: '', address: '', email: '' });
    }).catch(() => {});
  }, [selectedLocation]);

  const addPump = () => {
    const nextNr = pumps.length > 0 ? Math.max(...pumps.map(p => p.number)) + 1 : 1;
    setPumps(prev => [...prev, { number: nextNr, fuels: ['Diesel'] }]);
  };

  const removePump = (idx) => setPumps(prev => prev.filter((_, i) => i !== idx));

  const toggleFuel = (pumpIdx, fuel) => {
    setPumps(prev => prev.map((p, i) => {
      if (i !== pumpIdx) return p;
      const fuels = p.fuels.includes(fuel)
        ? p.fuels.filter(f => f !== fuel)
        : [...p.fuels, fuel];
      return { ...p, fuels };
    }));
  };

  const handleSave = async () => {
    if (!selectedLocation) { toast.error('Kein Standort ausgewählt'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      try {
        await axios.put(`${API}/api/wegfahrer/config/pumps/${selectedLocation}`, { pumps }, { headers: h });
      } catch (e) {
        toast.error(e.response?.data?.detail || `Fehler Zapfsäulen (${e.response?.status || 'Netzwerk'})`);
        setSaving(false); return;
      }
      try {
        await axios.put(`${API}/api/wegfahrer/config/lawyer/${selectedLocation}`, lawyer, { headers: h });
      } catch (e) {
        toast.error(e.response?.data?.detail || `Fehler Kanzlei (${e.response?.status || 'Netzwerk'})`);
        setSaving(false); return;
      }
      toast.success('Einstellungen gespeichert');
    } catch (err) {
      toast.error('Fehler: ' + (err.message || 'Bitte erneut versuchen'));
    } finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-xl mx-auto p-3 sm:p-4 md:p-6 space-y-4 pb-8">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/wegfahrer')} className="p-2 hover:bg-muted rounded-xl touch-manipulation">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-bold">Einstellungen</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">Zapfsäulen & Kanzlei-Daten</p>
          </div>
        </div>

        {/* Standort-Wahl (nur bei mehreren) */}
        {locations.length > 1 && (
          <Card>
            <CardContent className="p-3 sm:p-4">
              <Label className="text-sm">Standort</Label>
              <select value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)}
                className="mt-1.5 w-full h-11 px-3 rounded-xl border bg-background text-sm touch-manipulation">
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </CardContent>
          </Card>
        )}
        {locations.length === 1 && (
          <p className="text-xs text-muted-foreground px-1">
            Einstellungen für: <strong>{locations[0]?.name}</strong>
          </p>
        )}

        {/* Zapfsäulen */}
        <Card>
          <CardContent className="p-3 sm:p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Fuel className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                <h3 className="font-semibold text-sm sm:text-base">Zapfsäulen</h3>
              </div>
              <Button size="sm" variant="outline" onClick={addPump} className="gap-1 h-8 sm:h-9 touch-manipulation">
                <Plus className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Säule hinzufügen</span>
                <span className="sm:hidden">+ Säule</span>
              </Button>
            </div>

            {pumps.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Noch keine Zapfsäulen konfiguriert
              </p>
            ) : (
              <div className="space-y-3">
                {pumps.map((pump, idx) => (
                  <div key={idx} className="border rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">Säule {pump.number}</span>
                      <button onClick={() => removePump(idx)}
                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg touch-manipulation">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Fuel-Buttons – scrollbar auf kleinen Bildschirmen */}
                    <div className="flex flex-wrap gap-1.5">
                      {FUEL_OPTIONS.map(fuel => (
                        <button key={fuel} type="button" onClick={() => toggleFuel(idx, fuel)}
                          className={`px-2.5 py-1.5 rounded-full text-xs font-medium border transition-all touch-manipulation ${
                            pump.fuels.includes(fuel)
                              ? 'bg-orange-500 text-white border-orange-500'
                              : 'bg-background border-border text-muted-foreground hover:border-orange-300'
                          }`}>
                          {fuel}
                        </button>
                      ))}
                    </div>

                    {/* Benutzerdefinierte Kraftstoffe */}
                    {pump.fuels.filter(f => !FUEL_OPTIONS.includes(f)).map(customF => (
                      <div key={customF} className="flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-500 text-white">{customF}</span>
                        <button type="button" onClick={() => toggleFuel(idx, customF)}
                          className="text-xs text-red-500 touch-manipulation">✕</button>
                      </div>
                    ))}

                    {/* Freitext Kraftstoff */}
                    <div className="flex items-center gap-2">
                      <Input type="text" placeholder="Eigener Kraftstoff…"
                        value={customFuel[idx] || ''}
                        onChange={e => setCustomFuel(prev => ({...prev, [idx]: e.target.value}))}
                        className="h-9 text-sm flex-1" />
                      <Button type="button" size="sm" disabled={!customFuel[idx]?.trim()}
                        onClick={() => {
                          const name = customFuel[idx]?.trim();
                          if (!name || pump.fuels.includes(name)) return;
                          setPumps(prev => prev.map((p, i) =>
                            i === idx ? {...p, fuels: [...p.fuels, name]} : p
                          ));
                          setCustomFuel(prev => ({...prev, [idx]: ''}));
                        }}
                        className="h-9 bg-orange-500 hover:bg-orange-600 shrink-0 touch-manipulation">
                        + Hinzufügen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Kanzlei */}
        <Card>
          <CardContent className="p-3 sm:p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Scale className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              <h3 className="font-semibold text-sm sm:text-base">Kanzlei-Daten <span className="text-muted-foreground font-normal text-xs">(optional)</span></h3>
            </div>
            <p className="text-xs text-muted-foreground">Für die PDF-Dokumentation</p>
            <div className="space-y-3">
              {[
                { label: 'Kanzlei-Name', key: 'name', placeholder: 'Rechtsanwalt Max Muster' },
                { label: 'Anschrift', key: 'address', placeholder: 'Musterstraße 1, 70000 Stuttgart' },
                { label: 'E-Mail', key: 'email', placeholder: 'kanzlei@muster.de', type: 'email' },
              ].map(({ label, key, placeholder, type }) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-sm">{label}</Label>
                  <Input type={type || 'text'} placeholder={placeholder} value={lawyer[key]}
                    onChange={e => setLawyer(l => ({...l, [key]: e.target.value}))}
                    className="h-11 text-base" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving}
          className="w-full h-12 text-base gap-2 touch-manipulation">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          Einstellungen speichern
        </Button>
      </div>
    </DashboardLayout>
  );
};

export default WegfahrerSettings;
